import { Loader2, Play } from "lucide-react";
import { useEffect, useState } from "react";

interface VideoEmbedProps {
  url: string;
  platform?: string;
  className?: string;
}

export const VideoEmbed = ({
  url,
  platform,
  className = "",
}: VideoEmbedProps) => {
  const [embedUrl, setEmbedUrl] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [isInstagram, setIsInstagram] = useState(false);
  const [isDirectVideo, setIsDirectVideo] = useState(false);

  useEffect(() => {
    const generateEmbedUrl = () => {
      try {
        if (
          platform?.toLowerCase() === "tiktok" ||
          url.includes("tiktok.com")
        ) {
          const videoId = url.split("/video/")[1]?.split("?")[0];
          if (videoId) {
            setEmbedUrl(`https://www.tiktok.com/embed/v2/${videoId}`);
          } else {
            setError("Could not extract TikTok video ID");
          }
        } else if (
          platform?.toLowerCase() === "instagram" ||
          url.includes("instagram.com")
        ) {
          setIsInstagram(true);
          // Extract Instagram post/reel ID and implement reel -> post workaround
          let postId = "";

          if (url.includes("/reel/")) {
            // Extract reel ID and convert to post format for better embedding
            postId = url.split("/reel/")[1]?.split("/")[0]?.split("?")[0];
          } else if (url.includes("/p/")) {
            // Standard post format
            postId = url.split("/p/")[1]?.split("/")[0]?.split("?")[0];
          } else if (url.includes("/tv/")) {
            // IGTV format
            postId = url.split("/tv/")[1]?.split("/")[0]?.split("?")[0];
          }

          if (postId) {
            // Use /p/ format for better thumbnail display (the workaround)
            setEmbedUrl(`https://www.instagram.com/p/${postId}/embed/`);
          } else {
            setError("Could not extract Instagram post ID");
          }
        } else if (
          platform?.toLowerCase() === "youtube" ||
          url.includes("youtube.com") ||
          url.includes("youtu.be")
        ) {
          let videoId = "";
          if (url.includes("youtu.be")) {
            videoId = url.split("youtu.be/")[1]?.split("?")[0];
          } else if (url.includes("/shorts/")) {
            // Handle YouTube Shorts
            videoId = url.split("/shorts/")[1]?.split("?")[0];
          } else if (url.includes("youtube.com")) {
            videoId = url.split("v=")[1]?.split("&")[0];
          }

          if (videoId) {
            setEmbedUrl(`https://www.youtube.com/embed/${videoId}`);
          } else {
            setError("Could not extract YouTube video ID");
          }
        } else {
          // Check if it's a direct video URL (blob, data, or file URL)
          const videoExtensions = [
            ".mp4",
            ".webm",
            ".mov",
            ".avi",
            ".mkv",
            ".m4v",
          ];
          const isVideoFile = videoExtensions.some((ext) =>
            url.toLowerCase().includes(ext),
          );
          const isBlobOrDataUrl =
            url.startsWith("blob:") ||
            url.startsWith("data:") ||
            url.startsWith("http");

          if (isBlobOrDataUrl || isVideoFile || !url.includes(".com")) {
            // It's likely a direct video URL (uploaded file)
            setIsDirectVideo(true);
            setEmbedUrl(url);
          } else {
            setError("Unsupported platform");
          }
        }
      } catch (err) {
        setError("Error generating embed URL");
      } finally {
        setIsLoading(false);
      }
    };

    generateEmbedUrl();
  }, [url, platform]);

  if (isLoading) {
    return (
      <div
        className={`aspect-[9/16] bg-secondary/20 flex items-center justify-center rounded-lg ${className}`}
      >
        <Loader2 className="animate-spin h-6 w-6" />
      </div>
    );
  }

  if (error || !embedUrl) {
    const isInstagramError =
      platform?.toLowerCase() === "instagram" || url.includes("instagram.com");

    return (
      <div
        className={`aspect-[9/16] bg-gradient-to-br from-primary/10 to-secondary/20 flex flex-col items-center justify-center relative cursor-pointer group rounded-lg border border-border ${className}`}
        onClick={() => window.open(url, "_blank")}
      >
        <div className="flex flex-col items-center gap-3">
          {isInstagramError ? (
            <>
              <div className="w-12 h-12 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 rounded-xl flex items-center justify-center">
                <span className="text-white font-bold text-lg">IG</span>
              </div>
              <div className="text-center">
                <p className="text-sm font-medium text-primary">
                  Instagram Video
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Unable to load embed
                </p>
              </div>
            </>
          ) : (
            <>
              <Play className="h-8 w-8 text-primary/60 group-hover:text-primary transition-colors" />
              <div className="text-center">
                <p className="text-sm font-medium text-primary">
                  {platform?.toUpperCase() || "VIDEO"}
                </p>
              </div>
            </>
          )}
        </div>
        <div className="absolute top-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
          {platform?.toUpperCase() || "VIDEO"}
        </div>
        <div className="absolute bottom-2 left-2 text-xs text-white bg-black/70 px-2 py-1 rounded">
          Click to view
        </div>
      </div>
    );
  }

  // Direct video file (uploaded videos)
  if (isDirectVideo && embedUrl) {
    return (
      <div
        className={`aspect-[9/16] relative rounded-lg overflow-hidden bg-black ${className}`}
      >
        <video
          src={embedUrl}
          controls
          muted
          className="absolute top-0 left-0 w-full h-full object-contain"
          preload="metadata"
        />
      </div>
    );
  }

  // Instagram-specific styling to crop out the white UI elements
  if (isInstagram) {
    return (
      <div
        className={`aspect-[9/16] relative rounded-lg overflow-hidden bg-black ${className}`}
      >
        <iframe
          src={embedUrl}
          width="100%"
          height="100%"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="absolute top-0 left-0 w-full h-full rounded-lg"
          loading="lazy"
          scrolling="no"
          style={{
            overflow: "hidden",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            clipPath: "inset(10% 0 21% 0)", // Crop top 10% and bottom 21%
          }}
        />
        {/* Instagram logo overlay */}
        <div className="absolute top-2 right-2 w-6 h-6 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 rounded-lg flex items-center justify-center z-10">
          <span className="text-white font-bold text-xs">IG</span>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`aspect-[9/16] relative rounded-lg overflow-hidden ${className}`}
    >
      <iframe
        src={embedUrl}
        width="100%"
        height="100%"
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        className="rounded-lg"
        loading="lazy"
        scrolling="no"
        style={{
          overflow: "hidden",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
      />
    </div>
  );
};
